import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowRight, BookOpen, Calendar, Download, FileText, Play, Search } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SEOHead } from "@/components/seo/SEOHead";

const Resources = () => {
  const guides = [
    {
      title: "The Complete Product Launch Checklist",
      description: "A comprehensive checklist to ensure you don't miss any critical steps in your product launch.",
      image: "https://placehold.co/600x400/e2e8f0/64748b?text=Launch+Checklist",
      category: "Guide",
      readTime: "15 min read"
    },
    {
      title: "How to Create a Marketing Strategy for Your First Product",
      description: "Learn how to develop a marketing strategy that will help your product reach the right audience.",
      image: "https://placehold.co/600x400/e2e8f0/64748b?text=Marketing+Strategy",
      category: "Guide",
      readTime: "12 min read"
    },
    {
      title: "Building a Community Around Your Product",
      description: "Strategies for building and engaging with a community of users and advocates.",
      image: "https://placehold.co/600x400/e2e8f0/64748b?text=Community+Building",
      category: "Guide",
      readTime: "10 min read"
    },
    {
      title: "Content Marketing for Developers",
      description: "How to create technical content that attracts and engages your target audience.",
      image: "https://placehold.co/600x400/e2e8f0/64748b?text=Content+Marketing",
      category: "Guide",
      readTime: "8 min read"
    }
  ];

  const templates = [
    {
      title: "Product Hunt Launch Email Template",
      description: "Email template for notifying your audience about your Product Hunt launch.",
      category: "Email",
      downloadLink: "#"
    },
    {
      title: "Social Media Announcement Templates",
      description: "Templates for announcing your product on Twitter, LinkedIn, and other platforms.",
      category: "Social Media",
      downloadLink: "#"
    },
    {
      title: "Press Release Template",
      description: "A professional press release template for your product launch.",
      category: "PR",
      downloadLink: "#"
    },
    {
      title: "Product Launch Blog Post Template",
      description: "Structure and content outline for your product launch blog post.",
      category: "Blog",
      downloadLink: "#"
    },
    {
      title: "Launch Day Schedule Template",
      description: "Hour-by-hour schedule template for your product launch day.",
      category: "Planning",
      downloadLink: "#"
    },
    {
      title: "Customer Feedback Survey Template",
      description: "Survey template to collect feedback from early users.",
      category: "Research",
      downloadLink: "#"
    }
  ];

  const webinars = [
    {
      title: "From Idea to Launch: A Developer's Journey",
      description: "Learn from developers who successfully launched their first product.",
      date: "June 15, 2024",
      image: "https://placehold.co/600x400/e2e8f0/64748b?text=Webinar+1",
      registerLink: "#"
    },
    {
      title: "Marketing Strategies for Technical Founders",
      description: "Practical marketing advice for developers and technical founders.",
      date: "June 22, 2024",
      image: "https://placehold.co/600x400/e2e8f0/64748b?text=Webinar+2",
      registerLink: "#"
    },
    {
      title: "Building in Public: Leveraging Social Media for Product Growth",
      description: "How to use the 'build in public' approach to grow your audience.",
      date: "June 29, 2024",
      image: "https://placehold.co/600x400/e2e8f0/64748b?text=Webinar+3",
      registerLink: "#"
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Resources — Guides and templates for product launches | LaunchGuide.io"
        description="Guides, templates, and resources to help product managers and developers successfully launch their first product."
        pathname="/resources"
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="py-20 px-4 md:px-6 lg:px-8 bg-gradient-to-b from-blue-50 to-background dark:from-blue-950/20 dark:to-background">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block rounded-full bg-blue-100 dark:bg-blue-900/30 px-3 py-1 text-sm text-blue-700 dark:text-blue-300 mb-4">
              Resources
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Launch Your Product <span className="gradient-text">Like a Pro</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Guides, templates, and resources to help product managers and developers successfully launch their first product
            </p>
          </div>

          {/* Search Bar */}
          <div className="max-w-2xl mx-auto mb-16">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-5 w-5" />
              <input 
                type="text" 
                placeholder="Search for resources..." 
                className="w-full pl-10 pr-4 py-3 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
              />
            </div>
          </div>

          {/* Resource Tabs */}
          <Tabs defaultValue="guides" className="w-full">
            <TabsList className="grid grid-cols-3 max-w-md mx-auto mb-12">
              <TabsTrigger value="guides">Guides</TabsTrigger>
              <TabsTrigger value="templates">Templates</TabsTrigger>
              <TabsTrigger value="webinars">Webinars</TabsTrigger>
            </TabsList>
            
            {/* Guides Tab */}
            <TabsContent value="guides">
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                {guides.map((guide, index) => (
                  <Card key={index} className="overflow-hidden group hover:shadow-md transition-all">
                    <div className="aspect-video overflow-hidden">
                      <img 
                        src={guide.image} 
                        alt={guide.title} 
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                          {guide.category}
                        </span>
                        <span className="text-xs text-muted-foreground">{guide.readTime}</span>
                      </div>
                      <h3 className="font-bold mb-2">{guide.title}</h3>
                      <p className="text-sm text-muted-foreground mb-4">{guide.description}</p>
                      <Button variant="ghost" className="group p-0" asChild>
                        <Link to="#" className="flex items-center text-blue-600 dark:text-blue-400 text-sm">
                          Read guide
                          <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                        </Link>
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
              <div className="text-center mt-12">
                <Button variant="outline" asChild>
                  <Link to="#">
                    View all guides <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </TabsContent>
            
            {/* Templates Tab */}
            <TabsContent value="templates">
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {templates.map((template, index) => (
                  <Card key={index} className="p-6 hover:shadow-md transition-all">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-xs font-medium px-2.5 py-0.5 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                        {template.category}
                      </span>
                      <FileText className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <h3 className="font-bold mb-2">{template.title}</h3>
                    <p className="text-sm text-muted-foreground mb-4">{template.description}</p>
                    <Button variant="outline" size="sm" className="w-full" asChild>
                      <a href={template.downloadLink} className="flex items-center justify-center">
                        <Download className="mr-2 h-4 w-4" /> Download Template
                      </a>
                    </Button>
                  </Card>
                ))}
              </div>
              <div className="text-center mt-12">
                <Button variant="outline" asChild>
                  <Link to="#">
                    View all templates <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </TabsContent>
            
            {/* Webinars Tab */}
            <TabsContent value="webinars">
              <div className="grid md:grid-cols-3 gap-8">
                {webinars.map((webinar, index) => (
                  <Card key={index} className="overflow-hidden group hover:shadow-md transition-all">
                    <div className="aspect-video relative overflow-hidden">
                      <img 
                        src={webinar.image} 
                        alt={webinar.title} 
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="h-16 w-16 rounded-full bg-white/20 flex items-center justify-center">
                          <Play className="h-8 w-8 text-white" />
                        </div>
                      </div>
                    </div>
                    <div className="p-6">
                      <div className="flex items-center mb-3">
                        <Calendar className="h-4 w-4 text-muted-foreground mr-2" />
                        <span className="text-xs text-muted-foreground">{webinar.date}</span>
                      </div>
                      <h3 className="font-bold mb-2">{webinar.title}</h3>
                      <p className="text-sm text-muted-foreground mb-4">{webinar.description}</p>
                      <Button className="w-full bg-primary hover:bg-primary/90" asChild>
                        <a href={webinar.registerLink}>
                          Register Now
                        </a>
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
              <div className="text-center mt-12">
                <Button variant="outline" asChild>
                  <Link to="#">
                    View all webinars <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>
      
      {/* Newsletter Section */}
      <section className="py-20 px-4 md:px-6 lg:px-8">
        <div className="container mx-auto">
          <div className="max-w-4xl mx-auto bg-gradient-to-r from-blue-600 to-sky-500 rounded-xl p-12 text-white">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h2 className="text-3xl font-bold mb-4">Stay Updated</h2>
                <p className="mb-6">
                  Subscribe to our newsletter to receive the latest resources, guides, and tips for your product launch.
                </p>
                <div className="flex items-center space-x-4">
                  <BookOpen className="h-12 w-12" />
                  <div>
                    <p className="font-medium">Weekly Newsletter</p>
                    <p className="text-sm text-blue-100">Join 10,000+ product managers & developers</p>
                  </div>
                </div>
              </div>
              <div>
                <form className="space-y-4">
                  <div>
                    <input 
                      type="email" 
                      placeholder="Enter your email" 
                      className="w-full px-4 py-3 rounded-lg border border-white/20 bg-white/10 text-white placeholder:text-white/60 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent"
                    />
                  </div>
                  <Button className="w-full bg-white text-blue-600 hover:bg-blue-50">
                    Subscribe
                  </Button>
                  <p className="text-xs text-blue-100 text-center">
                    We respect your privacy. Unsubscribe at any time.
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Resources; 